/**
 * Message actions: history pages, send (with a pending copy until the
 * server answers), edit, delete, reactions, pins, typing, read marks.
 *
 * A message with images goes through up to three phases, each shown on its
 * pending copy: "optimizing" (sent before its images were ready: it waits
 * for them, and can be cancelled), "uploading" (cancellable) and, once
 * every byte is up, "processing" (the server re-encodes the images and
 * writes the message). Processing cannot be cancelled, so no Cancel is
 * offered then: the message is about to appear.
 */
import {
  ApiErrorCode,
  compareSnowflakes,
  emojiOfToken,
  Permissions,
  sameReactionEmoji,
  type Message,
  type MessageReference,
  type Snowflake,
} from '@neosocial/protocol';
import { create } from 'zustand';
import { tr } from '../i18n';
import { api, isApiRequestError } from '../lib/api';
import { failToast } from '../mod/actions';
import { toast } from '../ui/overlay';
import { updateData, useData } from './data';
import { applyAround, applyHistory, applyNewer, trimLoaded, upsertMessage } from './reducer';
import { can, channelPermissions } from './selectors';
import { noteSlowmodeWait } from './slowmode';

export const PAGE = 50;

export interface PendingMessage {
  nonce: string;
  channelId: Snowflake;
  content: string;
  replyTo: Snowflake | null;
  /** A reply that does not ping the replied-to person (the reply bar's @ OFF). */
  noReplyPing?: boolean;
  files: File[];
  /** waiting: refused for sending too fast; it goes out by itself at `until`. */
  state: 'sending' | 'waiting' | 'failed';
  at: number;
  /** While waiting: when it is sent again (unix ms). */
  until?: number;
  /** While sending a message with images: what is happening to them. */
  phase?: 'optimizing' | 'uploading' | 'processing';
  /** Progress of the optimizing or the upload, 0..1. */
  progress?: number;
  /** Images still being optimized (`files` is empty until they are ready). */
  fileCount?: number;
  /**
   * Failed because an AutoMod rule blocked it (2026-10-29): the row says so,
   * with the rule's response (`nx_automod_message`), and offers no retry.
   */
  automod?: { message: string | null };
}

/**
 * Images handed over while still being optimized: the message waits for
 * them (`sendMessage`'s `preparing`).
 */
export interface PreparedFiles {
  /** How many images. */
  count: number;
  /** The files to send once ready (an image that could not be used is left out). */
  ready: Promise<File[]>;
  /** Calls `cb` with the progress, 0..1, now and as it moves. */
  onProgress(cb: (fraction: number) => void): void;
  /** Stops the optimizing. */
  abort(): void;
}

/** Sends with files in flight, so the user can cancel an upload. */
const uploads = new Map<string, AbortController>();
/** Sends still waiting for their images to be optimized. */
const preparing = new Map<string, PreparedFiles>();

interface MsgUi {
  /** channelId -> what is loading */
  loading: Record<Snowflake, 'initial' | 'older' | 'newer' | 'around' | undefined>;
  /** channelId -> the last history load failed */
  failed: Record<Snowflake, boolean>;
  pending: Record<Snowflake, PendingMessage[]>;
  set(fn: (s: MsgUi) => Partial<MsgUi>): void;
}

export const useMsgUi = create<MsgUi>((set) => ({
  loading: {},
  failed: {},
  pending: {},
  set: (fn) => set(fn),
}));

export async function loadHistory(channelId: Snowflake, older = false): Promise<void> {
  const ui = useMsgUi.getState();
  if (ui.loading[channelId]) return;
  const cm = useData.getState().messages[channelId];
  // after a fresh READY the latest page comes first (older pages would leave a hole)
  if (older && cm?.stale) older = false;
  if (older && (!cm?.loaded || !cm.hasMore)) return;
  if (!older && cm?.loaded && !cm.stale) return;
  const before = older ? cm?.ids[0] : undefined;
  ui.set((s) => ({
    loading: { ...s.loading, [channelId]: older ? 'older' : 'initial' },
    failed: { ...s.failed, [channelId]: false },
  }));
  try {
    const page = await api.channels.messages(channelId, {
      limit: PAGE,
      ...(before ? { before } : {}),
    });
    updateData((s) => applyHistory(s, channelId, page, PAGE, older));
  } catch {
    ui.set((s) => ({ failed: { ...s.failed, [channelId]: true } }));
  } finally {
    ui.set((s) => ({ loading: { ...s.loading, [channelId]: undefined } }));
  }
}

/**
 * Loads the page of history around `messageId` (a jump from a pin, a reply's
 * quote line or a search result). True when the message is now loaded;
 * false when it is gone (deleted, or history can't be read).
 */
export async function loadAround(channelId: Snowflake, messageId: Snowflake): Promise<boolean> {
  if (useData.getState().messages[channelId]?.byId[messageId]) return true;
  const ui = useMsgUi.getState();
  if (ui.loading[channelId]) return false;
  ui.set((s) => ({ loading: { ...s.loading, [channelId]: 'around' } }));
  try {
    const page = await api.channels.messages(channelId, { limit: PAGE, around: messageId });
    if (!page.some((m) => m.id === messageId)) return false;
    updateData((s) => applyAround(s, channelId, messageId, page, PAGE));
    return true;
  } catch {
    return false;
  } finally {
    ui.set((s) => ({ loading: { ...s.loading, [channelId]: undefined } }));
  }
}

/** After a jump: the next page of newer history (as you scroll down towards the present). */
export async function loadNewer(channelId: Snowflake): Promise<void> {
  const ui = useMsgUi.getState();
  if (ui.loading[channelId]) return;
  const cm = useData.getState().messages[channelId];
  const after = cm?.ids.at(-1);
  if (!cm?.loaded || !cm.hasNewer || !after) return;
  ui.set((s) => ({
    loading: { ...s.loading, [channelId]: 'newer' },
    failed: { ...s.failed, [channelId]: false },
  }));
  try {
    const page = await api.channels.messages(channelId, { limit: PAGE, after });
    updateData((s) => applyNewer(s, channelId, page, PAGE));
    // a message sent while that page was on its way was not appended (the window had a gap)
    const d = useData.getState();
    const now = d.messages[channelId];
    const last = d.channels[channelId]?.last_message_id;
    if (
      now &&
      !now.hasNewer &&
      last &&
      now.ids.length &&
      compareSnowflakes(last, now.ids.at(-1)!) > 0
    )
      updateData((s) => ({
        ...s,
        messages: { ...s.messages, [channelId]: { ...now, hasNewer: true } },
      }));
  } catch {
    ui.set((s) => ({ failed: { ...s.failed, [channelId]: true } }));
  } finally {
    ui.set((s) => ({ loading: { ...s.loading, [channelId]: undefined } }));
  }
}

/**
 * Keeps only the newest `max` loaded messages of a channel (a window of older
 * ones is forgotten): a channel the reader left keeps one page, so visiting
 * channels does not pile up their history; the open one, pinned to its end,
 * keeps the window (MAX_LOADED). Not while one of its pages is loading.
 */
export function trimChannel(channelId: Snowflake, max: number = PAGE): void {
  if (useMsgUi.getState().loading[channelId]) return;
  updateData((s) => trimLoaded(s, channelId, max));
}

/**
 * Forgets the channel's loaded history and loads the newest page again:
 * "Jump to present", and when Read Message History is granted again.
 */
export function reloadHistory(channelId: Snowflake): Promise<void> {
  updateData((s) => {
    const messages = { ...s.messages };
    delete messages[channelId];
    return { ...s, messages };
  });
  return loadHistory(channelId);
}

function nonce(): string {
  return `${Date.now()}${Math.floor(Math.random() * 1e6)}`.slice(0, 25);
}

function setPending(channelId: Snowflake, fn: (list: PendingMessage[]) => PendingMessage[]): void {
  useMsgUi
    .getState()
    .set((s) => ({ pending: { ...s.pending, [channelId]: fn(s.pending[channelId] ?? []) } }));
}

/** A 429 waits and sends again by itself up to this long; longer, it fails with the wait shown. */
const MAX_AUTO_WAIT_S = 120;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
/** One send at a time per channel, so a wait keeps the messages in the order they were typed. */
const queues: Record<Snowflake, Promise<void> | undefined> = {};
const lastWaitToast: Record<Snowflake, number> = {};

/**
 * Sends after the channel's earlier sends; with none under way it starts at
 * once (an upload's progress and Cancel apply from the first byte).
 */
function enqueue(p: PendingMessage): void {
  const prev = queues[p.channelId];
  const tail: Promise<void> = (prev ? prev.then(() => deliver(p)) : deliver(p)).finally(() => {
    if (queues[p.channelId] === tail) delete queues[p.channelId];
  });
  queues[p.channelId] = tail;
}

function isPending(p: PendingMessage): boolean {
  return !!useMsgUi.getState().pending[p.channelId]?.some((x) => x.nonce === p.nonce);
}

/** Why a send was refused for speed: slowmode in this channel, or the burst limit. */
function waitToast(channelId: Snowflake, seconds: number): void {
  const now = Date.now();
  if (now - (lastWaitToast[channelId] ?? 0) < seconds * 1000) return;
  lastWaitToast[channelId] = now;
  const d = useData.getState();
  const ch = d.channels[channelId];
  // the server's slowmode skips these (messages.service create())
  const bypass = [
    Permissions.BYPASS_SLOWMODE,
    Permissions.MANAGE_MESSAGES,
    Permissions.MANAGE_CHANNELS,
  ];
  const perms = ch ? channelPermissions(d, ch) : null;
  const slow =
    !!ch?.guild_id &&
    (ch.rate_limit_per_user ?? 0) > 0 &&
    !!perms &&
    !bypass.some((b) => can(perms, b));
  const { t } = tr();
  // the composer counts it down too (store/slowmode.ts)
  if (slow) noteSlowmodeWait(channelId, seconds);
  toast(t(slow ? 'ns.msg.slowmode' : 'ns.msg.rateLimited', { n: Math.ceil(seconds) }), {
    icon: 'clock',
    tone: 'warn',
  });
}

async function deliver(p: PendingMessage): Promise<void> {
  const ref: MessageReference | undefined = p.replyTo
    ? { message_id: p.replyTo, fail_if_not_exists: false }
    : undefined;
  for (let attempt = 0; ; attempt++) {
    // discarded (or cancelled) while it waited
    if (!isPending(p)) return;
    const ctl = new AbortController();
    if (p.files.length) uploads.set(p.nonce, ctl);
    // Progress in whole percent steps, so the store is not written per packet.
    // Every byte up: the server is processing the images now (no cancel).
    let shown = -1;
    const onUploadProgress = (f: number) => {
      const pct = Math.floor(f * 100);
      if (pct === shown) return;
      shown = pct;
      setPending(p.channelId, (l) =>
        l.map((x) =>
          x.nonce === p.nonce
            ? { ...x, progress: pct / 100, phase: pct >= 100 ? 'processing' : 'uploading' }
            : x,
        ),
      );
    };
    try {
      const m = await api.channels.send(
        p.channelId,
        {
          content: p.content,
          nonce: p.nonce,
          enforce_nonce: true,
          ...(ref ? { message_reference: ref } : {}),
          // the default parse, minus the reply's ping (the server's default otherwise)
          ...(ref && p.noReplyPing
            ? {
                allowed_mentions: {
                  parse: ['users', 'roles', 'everyone'] as Array<'users' | 'roles' | 'everyone'>,
                  replied_user: false,
                },
              }
            : {}),
        },
        p.files,
        p.files.length ? { onUploadProgress, signal: ctl.signal } : {},
      );
      updateData((s) => {
        const cm = s.messages[m.channel_id];
        return cm?.loaded && !cm.hasNewer ? upsertMessage(s, m) : s;
      });
      setPending(p.channelId, (l) => l.filter((x) => x.nonce !== p.nonce));
      return;
    } catch (e) {
      // cancelled by the user: the pending copy is already gone
      if (ctl.signal.aborted) return;
      // sent too fast (the burst limit or slowmode), with or without images: wait as the
      // server says, then send again. The image queue or budget's 429 says so
      // (`nx_image_queue`): that one fails with its reason (fix round 2026-10-28: a second
      // image in slowmode failed at once instead of waiting).
      const imageGate = isApiRequestError(e) && e.status === 429 && !!e.body?.nx_image_queue;
      if (isApiRequestError(e) && e.status === 429 && !imageGate && attempt < 5) {
        const wait = Math.max(0.2, e.retryAfter ?? 1);
        waitToast(p.channelId, wait);
        if (wait <= MAX_AUTO_WAIT_S) {
          const until = Date.now() + wait * 1000;
          setPending(p.channelId, (l) =>
            l.map((x) => (x.nonce === p.nonce ? { ...x, state: 'waiting', until } : x)),
          );
          await sleep(wait * 1000 + 100);
          setPending(p.channelId, (l) =>
            l.map((x) => (x.nonce === p.nonce ? { ...x, state: 'sending', until: undefined } : x)),
          );
          continue;
        }
      }
      // AutoMod blocked it: the row says so, with the rule's response (no toast)
      const automod =
        isApiRequestError(e) && e.code === ApiErrorCode.AUTOMOD_MESSAGE_BLOCKED
          ? { message: e.body?.nx_automod_message ?? null }
          : undefined;
      setPending(p.channelId, (l) =>
        l.map((x) =>
          x.nonce === p.nonce
            ? {
                ...x,
                state: 'failed',
                until: undefined,
                progress: undefined,
                phase: undefined,
                ...(automod ? { automod } : {}),
              }
            : x,
        ),
      );
      if (automod) return;
      // say why when the reason is about the sender (timeout, blocks, missing permission);
      // for images, also when the server asks to wait (its image queue or the hourly budget)
      if (
        isApiRequestError(e) &&
        e.status >= 400 &&
        e.status < 500 &&
        (e.status !== 429 || imageGate || p.files.length > 0)
      )
        failToast(e);
      return;
    } finally {
      uploads.delete(p.nonce);
    }
  }
}

/**
 * Stops a send that is optimizing or uploading and drops its pending
 * message. (Once the upload is complete the server is processing it and
 * the row offers no cancel: the message will appear.)
 */
export function cancelPending(channelId: Snowflake, n: string): void {
  preparing.get(n)?.abort();
  preparing.delete(n);
  uploads.get(n)?.abort();
  uploads.delete(n);
  dropPending(channelId, n);
}

/**
 * Sends a message. With `preparing`, its images are still being optimized:
 * the pending copy shows that ("Optimizing… n%") and the message goes as
 * soon as they are ready.
 */
export function sendMessage(
  channelId: Snowflake,
  content: string,
  o: {
    replyTo?: Snowflake | null;
    files?: File[];
    preparing?: PreparedFiles;
    /** false: the reply does not ping its author (allowed_mentions.replied_user). */
    pingReply?: boolean;
  } = {},
): void {
  const files = o.files ?? [];
  const p: PendingMessage = {
    nonce: nonce(),
    channelId,
    content,
    replyTo: o.replyTo ?? null,
    ...(o.replyTo && o.pingReply === false ? { noReplyPing: true } : {}),
    files,
    state: 'sending',
    at: Date.now(),
    ...(o.preparing
      ? { phase: 'optimizing' as const, progress: 0, fileCount: o.preparing.count }
      : files.length
        ? { phase: 'uploading' as const, progress: 0 }
        : {}),
  };
  setPending(channelId, (l) => [...l, p]);
  const prep = o.preparing;
  if (!prep) {
    enqueue(p);
    return;
  }
  preparing.set(p.nonce, prep);
  let shown = -1;
  prep.onProgress((f) => {
    const pct = Math.floor(f * 100);
    if (pct === shown || !preparing.has(p.nonce)) return;
    shown = pct;
    setPending(channelId, (l) =>
      l.map((x) => (x.nonce === p.nonce ? { ...x, progress: pct / 100 } : x)),
    );
  });
  void prep.ready.then(
    (ready) => {
      if (preparing.get(p.nonce) !== prep) return; // cancelled
      preparing.delete(p.nonce);
      // every image failed (each one already said why) and there is no text
      if (!ready.length && !content.trim()) {
        dropPending(channelId, p.nonce);
        return;
      }
      const next: PendingMessage = {
        ...p,
        files: ready,
        fileCount: undefined,
        phase: ready.length ? 'uploading' : undefined,
        progress: ready.length ? 0 : undefined,
      };
      setPending(channelId, (l) => l.map((x) => (x.nonce === p.nonce ? next : x)));
      enqueue(next);
    },
    () => {
      if (preparing.get(p.nonce) !== prep) return;
      preparing.delete(p.nonce);
      dropPending(channelId, p.nonce);
    },
  );
}

export function retryPending(channelId: Snowflake, n: string): void {
  const p = useMsgUi.getState().pending[channelId]?.find((x) => x.nonce === n);
  if (!p) return;
  const again: PendingMessage = {
    ...p,
    state: 'sending',
    ...(p.files.length ? { phase: 'uploading' as const, progress: 0 } : {}),
  };
  setPending(channelId, (l) => l.map((x) => (x.nonce === n ? again : x)));
  enqueue(again);
}

export function dropPending(channelId: Snowflake, n: string): void {
  setPending(channelId, (l) => l.filter((x) => x.nonce !== n));
}

/** MESSAGE_CREATE with our nonce settles the pending copy too. */
export function settlePending(m: Message): void {
  if (!m.nonce) return;
  const list = useMsgUi.getState().pending[m.channel_id];
  if (list?.some((x) => x.nonce === m.nonce))
    setPending(m.channel_id, (l) => l.filter((x) => x.nonce !== m.nonce));
}

export async function editMessage(
  channelId: Snowflake,
  id: Snowflake,
  content: string,
): Promise<void> {
  const m = await api.channels.edit(channelId, id, { content });
  updateData((s) => upsertMessage(s, m));
}

export async function deleteMessage(channelId: Snowflake, id: Snowflake): Promise<void> {
  await api.channels.deleteMessage(channelId, id);
}

export async function toggleReaction(m: Message, emoji: string): Promise<void> {
  // a custom emoji is its token (<:name:id>), found by its ID (emoji.ts)
  const want = emojiOfToken(emoji);
  const mine = m.reactions?.find((r) => sameReactionEmoji(r.emoji, want))?.me;
  if (mine) await api.channels.unreact(m.channel_id, m.id, emoji);
  else await api.channels.react(m.channel_id, m.id, emoji);
}

const lastTyping: Record<Snowflake, number> = {};
export function sendTyping(channelId: Snowflake): void {
  const now = Date.now();
  if (now - (lastTyping[channelId] ?? 0) < 8000) return;
  lastTyping[channelId] = now;
  void api.channels.typing(channelId).catch(() => {});
}

const acked: Record<Snowflake, Snowflake> = {};
/** Marks the channel read up to its last message (once per message). */
export function ackChannel(channelId: Snowflake): void {
  const s = useData.getState();
  const last = s.channels[channelId]?.last_message_id;
  if (!last || acked[channelId] === last) return;
  const rs = s.readStates[channelId];
  if (rs?.last_message_id === last && !rs.mention_count) return;
  acked[channelId] = last;
  updateData((d) => ({
    ...d,
    readStates: {
      ...d.readStates,
      [channelId]: {
        ...d.readStates[channelId],
        id: channelId,
        last_message_id: last,
        mention_count: 0,
      },
    },
  }));
  void api.channels.ack(channelId, last).catch(() => {
    delete acked[channelId];
  });
}
